import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from '../../../context/NotificationContext';   
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const FaqList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
    const notify = useNotify();
  // State management
  const [loading, setLoading] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} FAQs`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
  });

  const getFaqs = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching FAQs with params:", requestParams);
        const response = await masterService.getFaqs(requestParams);
        console.log("FAQs API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const faqsData = response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("FAQs data:", faqsData);
          console.log("Pagination data:", paginationData);

          // Ensure faqsData is an array
          if (Array.isArray(faqsData)) {
            setFaqs(faqsData);
          } else {
            console.error("FAQs data is not an array:", faqsData);
            setFaqs([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch FAQs" });
          setFaqs([]);
        }
      } catch (error) {
        console.error("Error fetching FAQs:", error);
        notify.error({ message: "Failed to fetch FAQs" });
        setFaqs([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText]
  );

  // Load FAQs data
  useEffect(() => {
    setPageTitle("FAQs");
    getFaqs();
  }, [getFaqs, setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getFaqs({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getFaqs({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add FAQ
  const handleAddFaq = () => {
    navigate("/faqs/add/new");
  };

  // Handle edit FAQ
  const handleEditFaq = (faqId) => {
    navigate(`/faqs/edit/${faqId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Order",
      dataIndex: "faq_order",
      key: "faq_order",
      width: 80,
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.faq_order - b.faq_order,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text}</Text>,
    },
    {
      title: "Question",
      dataIndex: "faq_question",
      key: "faq_question",
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditFaq(record.faq_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            FAQs ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Search
              placeholder="Search FAQs..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddFaq}
              size="middle"
            >
              Add FAQ
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={faqs}
          rowKey="faq_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{
            emptyText: "No FAQs found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default FaqList;
